import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Tag,
  Descriptions,
  Table,
  Alert,
  Spin,
  message,
  Space,
  Modal,
  Progress,
  Tabs,
  Empty,
} from 'antd';
import {
  ArrowLeftOutlined,
  BankOutlined,
  GlobalOutlined,
  UserOutlined,
  DownloadOutlined,
  DollarOutlined,
  ReloadOutlined,
  TrophyOutlined,
  FileTextOutlined,
  EyeOutlined,
  CalendarOutlined,
  ExpandOutlined,
  NotificationOutlined,
  CheckCircleOutlined,
  RiseOutlined,
  FallOutlined,
} from '@ant-design/icons';
import LandingLayout from './LandingLayout';
import IssuerNews from './IssuerNews';
import ISINCard from '../shared/ISINCard';
import DefaultRiskPredictor from '../shared/DefaultRiskPredictor';
import { ThemeContext } from '../../context/ThemeContext';
import './IssuerDetails.css';
import apiClient from '../../services/apiClient';
import { LineChart } from '@mui/x-charts/LineChart';
import { formatCurrency, sortIssuerFinancialReportsDesc } from '../../constants/financialConstants';
import { formatDefaultRiskDate } from '../../utils/defaultRiskUtils';

const { Title, Text, Paragraph } = Typography;

/**
 * Map API `columnUsed` (e.g. "31-03-2021 (Audited)") to a clear badge — avoids comparing to literal "Audited".
 */
const getAuditBadgeFromColumnUsed = (columnUsed, auditStatusFallback) => {
  const raw =
    columnUsed != null && String(columnUsed).trim() !== ''
      ? String(columnUsed)
      : auditStatusFallback != null
        ? String(auditStatusFallback)
        : '';
  if (!raw) return { shortLabel: '—', color: 'default' };
  const lower = raw.toLowerCase();
  if (lower.includes('unaudited')) return { shortLabel: 'Unaudited', color: 'orange' };
  if (lower.includes('limited review') || lower.includes('limited-review'))
    return { shortLabel: 'Limited review', color: 'blue' };
  if (lower.includes('audited')) return { shortLabel: 'Audited', color: 'green' };
  return { shortLabel: raw.length > 32 ? `${raw.slice(0, 29)}…` : raw, color: 'default' };
};

const formatRatioBrief = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

/** NSE market cap is stored in absolute rupees; display in crores (1 Cr = ₹1,00,00,000). */
const RUPEES_PER_CRORE = 1e7;
const formatMarketCapInCr = (valueInRupees) => {
  if (valueInRupees === null || valueInRupees === undefined || valueInRupees === '') {
    return null;
  }
  const num = Number(valueInRupees);
  if (Number.isNaN(num)) {
    return String(valueInRupees);
  }
  return `${(num / RUPEES_PER_CRORE).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
};

/** Few headline numbers for preview / modal cards (quarterly may omit balance sheet). */
const getFinancialHighlightMetrics = (report) => {
  const rows = [
    { label: 'Total income', value: formatCurrency(report?.totalIncome) },
    { label: 'Profit after tax', value: formatCurrency(report?.profitLossForPeriod) },
  ];
  if (report?.assets != null && report?.assets !== '') {
    rows.push({ label: 'Total assets', value: formatCurrency(report.assets) });
  }
  if (report?.borrowings != null && report?.borrowings !== '') {
    rows.push({ label: 'Borrowings', value: formatCurrency(report.borrowings) });
  }
  const de = formatRatioBrief(report?.debtEquityRatio);
  if (de != null && rows.length < 4) rows.push({ label: 'Debt / equity', value: de });
  if (report?.netWorth != null && report?.netWorth !== '' && rows.length < 4) {
    rows.push({ label: 'Net worth', value: formatCurrency(report.netWorth) });
  }
  return rows.slice(0, 4);
};

// Common function to format classification values (e.g., "REAL_ESTATE" -> "Real Estate")
const formatClassificationValue = (value) => {
  if (!value || value === 'N/A') return 'N/A';
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getIssuerInitials = (issuerName) => {
  if (!issuerName) return '';
  if (issuerName.includes('HDFC')) return 'HDFC';
  if (issuerName.includes('REC')) return 'REC';
  if (issuerName.includes('SBI')) return 'SBI';
  if (issuerName.includes('ICICI')) return 'ICICI';
  if (issuerName.includes('AXIS')) return 'AXIS';
  const words = issuerName.split(' ');
  if (words.length >= 2) {
    return words.slice(0, 2).map((word) => word.charAt(0)).join('');
  }
  return issuerName.substring(0, 3).toUpperCase();
};

const getIssuerLogoUrl = (issuer) => issuer?.logoUrl || issuer?.logo_url || null;

/** 0–100 for governance composite score display / progress. */
const clampPercentDisplay = (n) => Math.min(100, Math.max(0, Math.round(Number(n) || 0)));

const ANNOUNCEMENT_TYPE_LABELS = {
  FINANCIAL_ISIN: 'Financial ISIN',
  FINANCIAL_ISSUER: 'Financial Issuer',
  NON_FINANCIAL_BUSINESS: 'Non-Financial Business',
  NON_FINANCIAL_OTHER: 'Non-Financial Other',
};

const ANNOUNCEMENT_TYPE_COLORS = {
  FINANCIAL_ISIN: 'blue',
  FINANCIAL_ISSUER: 'green',
  NON_FINANCIAL_BUSINESS: 'orange',
  NON_FINANCIAL_OTHER: 'purple',
};

const getAnnouncementTypeKey = (announcement) =>
  announcement?.announcementType || announcement?.categoryType;

const IssuerDetails = ({ skipLayout = false }) => {
  const { issuerId: issuerIdFromParams } = useParams();
  const navigate = useNavigate();
  const { isDark } = useContext(ThemeContext);
  const searchParams = new URLSearchParams(window.location.search);
  const issuerIdFromQuery = searchParams.get('issuerId');

  // Use issuerId from URL params or query string
  const issuerId = issuerIdFromParams || issuerIdFromQuery;

  // Debug logging
  console.log('IssuerDetails - issuerIdFromParams:', issuerIdFromParams);
  console.log('IssuerDetails - issuerIdFromQuery:', issuerIdFromQuery);
  console.log('IssuerDetails - final issuerId:', issuerId);
  const [issuerData, setIssuerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isinsData, setIsinsData] = useState([]);
  const [isinsLoading, setIsinsLoading] = useState(false);
  const [issuerAnnualReports, setIssuerAnnualReports] = useState([]);
  const [issuerQuarterlyReports, setIssuerQuarterlyReports] = useState([]);
  const [issuerFinancialsLoading, setIssuerFinancialsLoading] = useState(false);
  const [financialIssuerData, setFinancialIssuerData] = useState(null);
  const [groupCompanyDetails, setGroupCompanyDetails] = useState(null);
  const [groupCompanyLoading, setGroupCompanyLoading] = useState(false);
  const [ratingsData, setRatingsData] = useState(null);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [promoterDetailsData, setPromoterDetailsData] = useState({});
  const [promoterDetailsLoading, setPromoterDetailsLoading] = useState(false);
  const [newsData, setNewsData] = useState(null);
  const [newsLoading, setNewsLoading] = useState(false);
  const [announcementsPreview, setAnnouncementsPreview] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [totalAnnouncements, setTotalAnnouncements] = useState(0);
  const [announcementModalTab, setAnnouncementModalTab] = useState('FINANCIAL_ISIN');
  const [modalAnnouncements, setModalAnnouncements] = useState([]);
  const [modalAnnouncementsLoading, setModalAnnouncementsLoading] = useState(false);
  const [modalAnnouncementsTotal, setModalAnnouncementsTotal] = useState(0);
  const [modalAnnouncementsPage, setModalAnnouncementsPage] = useState(1);
  const [modalAnnouncementsPageSize, setModalAnnouncementsPageSize] = useState(10);
  const [promoterScoreData, setPromoterScoreData] = useState(null);
  const [promoterScoreLoading, setPromoterScoreLoading] = useState(false);

  // Fixed Deposit rates
  const [fixedDepositRates, setFixedDepositRates] = useState([]);
  const [fixedDepositLoading, setFixedDepositLoading] = useState(false);
  const [fixedDepositError, setFixedDepositError] = useState(null);

  // Modal states
  const [modalVisible, setModalVisible] = useState({
    overview: false,
    isins: false,
    financials: false,
    news: false,
    groupCompanies: false,
    announcements: false,
    basicIndustry: false,
    bondBorrowings: false
  });
  const [basicIndustryIssuers, setBasicIndustryIssuers] = useState([]);
  const [basicIndustryLoading, setBasicIndustryLoading] = useState(false);
  const [bondBorrowingsData, setBondBorrowingsData] = useState([]);
  const [bondBorrowingsLoading, setBondBorrowingsLoading] = useState(false);
  const [issuerBorrowings, setIssuerBorrowings] = useState([]);
  const [issuerBorrowingsLoading, setIssuerBorrowingsLoading] = useState(false);
  const [issuerBorrowingsError, setIssuerBorrowingsError] = useState(null);
  const [latestMerton, setLatestMerton] = useState(null);
  const [latestMertonLoading, setLatestMertonLoading] = useState(false);
  const [mertonPdHistory, setMertonPdHistory] = useState([]);
  const [mertonHistoryLoading, setMertonHistoryLoading] = useState(false);
  const [pdChartFullscreenOpen, setPdChartFullscreenOpen] = useState(false);

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Helper function to map issuer name to stock symbol (case-insensitive)
  // Fetch ISINs data for the issuer
  const fetchIsinsData = async () => {
    setIsinsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/master-isin/cin/${issuerId}`);
      const result = response.data;

      if (result.statusCode === 200 && result.data && result.data.content && Array.isArray(result.data.content)) {
        setIsinsData(result.data.content);
        console.log('ISINs API Response:', result);
      } else {
        console.error('Invalid ISINs API response structure:', result);
        setIsinsData([]);
      }
    } catch (error) {
      console.error('Error fetching ISINs data:', error);
      setIsinsData([]);
    } finally {
      setIsinsLoading(false);
    }
  };

  // Fetch issuers by basic industry
  const fetchIssuersByBasicIndustry = async (basicIndustry) => {
    if (!basicIndustry) return;
    setBasicIndustryLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/master-issuer/basic-industry/${encodeURIComponent(basicIndustry)}`);
      const result = response.data;
      if (result.statusCode === 200 && result.data && Array.isArray(result.data)) {
        setBasicIndustryIssuers(result.data);
      } else {
        console.error('Invalid API response structure:', result);
        setBasicIndustryIssuers([]);
        message.error('Failed to fetch issuers');
      }
    } catch (error) {
      console.error('Error fetching issuers by basic industry:', error);
      setBasicIndustryIssuers([]);
      message.error('Error fetching issuers');
    } finally {
      setBasicIndustryLoading(false);
    }
  };

  // Handle basic industry click
  const handleBasicIndustryClick = () => {
    if (issuerData?.classification?.basicIndustry) {
      fetchIssuersByBasicIndustry(issuerData.classification.basicIndustry);
      openModal('basicIndustry');
    }
  };

  // Fetch bond borrowings (ISIN and amount raised) by issuer name
  const fetchBondBorrowings = async (issuerName) => {
    if (!issuerName) return;
    setBondBorrowingsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/master-isin/${encodeURIComponent(issuerName)}/amount-raised`);
      const result = response.data;
      if (result.statusCode === 200 && result.data && Array.isArray(result.data)) {
        setBondBorrowingsData(result.data);
      } else {
        console.error('Invalid API response structure:', result);
        setBondBorrowingsData([]);
        message.error('Failed to fetch bond borrowings');
      }
    } catch (error) {
      console.error('Error fetching bond borrowings:', error);
      setBondBorrowingsData([]);
      message.error('Error fetching bond borrowings');
    } finally {
      setBondBorrowingsLoading(false);
    }
  };

  // Handle total bond borrowings click
  const handleBondBorrowingsClick = () => {
    if (issuerData?.issuer) {
      fetchBondBorrowings(issuerData.issuer);
      openModal('bondBorrowings');
    }
  };

  const fetchIssuerBorrowings = async (issuerName) => {
    if (!issuerName) return;
    setIssuerBorrowingsLoading(true);
    setIssuerBorrowingsError(null);
    try {
      const response = await apiClient.get(`${baseUrl}/borrowing/issuer/${encodeURIComponent(issuerName)}`, {
        validateStatus: () => true,
      });
      if (response.status === 204 || response.status === 404) {
        setIssuerBorrowings([]);
        return;
      }
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;
      if (result.statusCode === 200 && Array.isArray(result?.data?.borrowings)) {
        setIssuerBorrowings(result.data.borrowings);
      } else {
        setIssuerBorrowings([]);
      }
    } catch (error) {
      console.error('Error fetching issuer borrowings:', error);
      setIssuerBorrowings([]);
      setIssuerBorrowingsError('Error fetching issuer borrowings');
    } finally {
      setIssuerBorrowingsLoading(false);
    }
  };

  // Fetch issuer financial reports using the new unified API
  const fetchIssuerFinancialReports = async (issuerName) => {
    setIssuerFinancialsLoading(true);
    setIssuerAnnualReports([]);
    setIssuerQuarterlyReports([]);
    try {
      const apiUrl = `${baseUrl}/issuer-financial-data/issuer?issuerName=${encodeURIComponent(issuerName)}`;
      console.log('Issuer Financial Reports API URL:', apiUrl);

      const response = await apiClient.get(apiUrl, {
        validateStatus: () => true,
      });
      console.log('Issuer Financial Reports Response Status:', response.status);

      if (response.status === 204 || response.status === 404) {
        console.log(`No issuer financial reports found for issuer:`, issuerName);
        setIssuerAnnualReports([]);
        setIssuerQuarterlyReports([]);
        return;
      }

      if (response.status < 200 || response.status >= 300) {
        console.error(`Error fetching issuer financial reports:`, response.status, response.statusText);
        setIssuerAnnualReports([]);
        setIssuerQuarterlyReports([]);
        return;
      }

      const result = response.data;
      console.log('Issuer Financial Reports API Response:', result);

      if (result.statusCode === 200 && result.data) {
        // The new API returns data in format: { issuer: {...}, annualReports: [...], quarterlyReports: [...] }
        const annualData = result.data.annualReports || [];
        const quarterlyData = result.data.quarterlyReports || [];
        const issuerInfo = result.data.issuer || null;

        setIssuerAnnualReports(annualData);
        setIssuerQuarterlyReports(quarterlyData);
        setFinancialIssuerData(issuerInfo);

        console.log(`Loaded ${annualData.length} annual reports and ${quarterlyData.length} quarterly reports`);
      } else {
        console.log('Invalid issuer financial report response structure:', result);
        setIssuerAnnualReports([]);
        setIssuerQuarterlyReports([]);
      }
    } catch (error) {
      console.error('Error fetching issuer financial reports:', error);
      setIssuerAnnualReports([]);
      setIssuerQuarterlyReports([]);
    } finally {
      setIssuerFinancialsLoading(false);
    }
  };

  // Fetch issuer data from API
  const fetchIssuerData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${baseUrl}/master-issuer/${issuerId}`);
      const result = response.data;

      // Handle the new API response structure
      if (result.statusCode === 200 && result.data) {
        setIssuerData(result.data);
        console.log('Issuer Details API Response:', result);
      } else {
        console.error('Invalid issuer details API response structure:', result);
        setError("Invalid response structure from API");
        message.error("Invalid response structure from API");
      }
    } catch (error) {
      console.error('Error fetching issuer data:', error);
      setError("Error fetching issuer data");
      message.error("Error fetching issuer data");
    } finally {
      setLoading(false);
    }
  };

  const fetchFixedDepositRates = async (issuerName) => {
    if (!issuerName) return;
    setFixedDepositLoading(true);
    setFixedDepositError(null);
    try {
      const response = await apiClient.get(
        `${baseUrl}/fixed-deposits/issuer/${encodeURIComponent(issuerName)}`,
        { validateStatus: () => true }
      );

      if (response.status === 204) {
        setFixedDepositRates([]);
        return;
      }

      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json = response.data;
      const content = json?.data?.content ?? [];
      setFixedDepositRates(Array.isArray(content) ? content : []);
    } catch (error) {
      console.error('Error fetching fixed deposit rates:', error);
      setFixedDepositError('Error fetching fixed deposit rates');
      setFixedDepositRates([]);
    } finally {
      setFixedDepositLoading(false);
    }
  };

  useEffect(() => {
    if (issuerId) {
      fetchIssuerData();
      fetchIsinsData();
    }
  }, [issuerId]);

  useEffect(() => {
    const issuerName = issuerData?.issuer;
    if (!issuerName || !baseUrl) {
      setLatestMerton(null);
      setMertonPdHistory([]);
      return;
    }

    const encodedIssuer = encodeURIComponent(issuerName);
    setLatestMertonLoading(true);
    setMertonHistoryLoading(true);

    Promise.all([
      apiClient.get(`${baseUrl}/credit-risk/merton/${encodedIssuer}/latest`, { validateStatus: () => true }),
      apiClient.get(`${baseUrl}/credit-risk/merton/${encodedIssuer}/pd-history`, { validateStatus: () => true }),
    ])
      .then(([latestRes, historyRes]) => {
        const latestData = latestRes?.data?.data || null;
        setLatestMerton(latestData);

        const historyData = Array.isArray(historyRes?.data?.data) ? historyRes.data.data : [];
        setMertonPdHistory(historyData);
      })
      .catch((err) => {
        console.error('Error fetching persisted credit risk daily data:', err);
        setLatestMerton(null);
        setMertonPdHistory([]);
      })
      .finally(() => {
        setLatestMertonLoading(false);
        setMertonHistoryLoading(false);
      });
  }, [issuerData?.issuer, baseUrl]);

  const defaultRiskData = useMemo(() => {
    if (!latestMerton) return null;
    return {
      issuerName: issuerData?.issuer,
      asOnDate: latestMerton.date,
      PD_percent: latestMerton.PD_percent ?? latestMerton.pd_percent,
      pdHistory: mertonPdHistory,
      risk: latestMerton.risk,
    };
  }, [issuerData?.issuer, latestMerton, mertonPdHistory]);

  const pdSeries = mertonPdHistory;
  const pdChartX = pdSeries.map((row) => formatDefaultRiskDate(row.tradeDate));
  const pdChartY = pdSeries.map((row) =>
    Number(row.PD_percent ?? row.pd_percent ?? 0)
  );
  const pdChartTickLabelInterval =
    pdChartX.length <= 14
      ? () => true
      : (_, index) =>
          index === 0 ||
          index === pdChartX.length - 1 ||
          index % Math.ceil(pdChartX.length / 12) === 0;
  const pdChartFullscreenHeight =
    typeof window !== 'undefined' ? Math.min(720, window.innerHeight - 200) : 520;

  // Fetch group company details
  const fetchGroupCompanyDetails = async (parentCompanyName) => {
    setGroupCompanyLoading(true);
    try {
      // Check if baseUrl already includes /api/v1/sb, if not add it
      const apiBaseUrl = baseUrl.includes('/api/v1/sb') ? baseUrl : `${baseUrl}/api/v1/sb`;
      const apiUrl = `${apiBaseUrl}/master-issuer/group-company-details?parentCompanyName=${encodeURIComponent(parentCompanyName)}`;
      console.log('Group Company Details API URL:', apiUrl);

      const response = await apiClient.get(apiUrl, { validateStatus: () => true });
      console.log('Group Company Details Response Status:', response.status);

      if (response.status === 204 || response.status === 404) {
        console.log(`No group company details found for parent company:`, parentCompanyName);
        setGroupCompanyDetails({
          associates: [],
          subsidiaries: [],
          joint_ventures: []
        });
        return;
      }

      if (response.status < 200 || response.status >= 300) {
        console.error(`Error fetching group company details:`, response.status, response.statusText);
        setGroupCompanyDetails({
          associates: [],
          subsidiaries: [],
          joint_ventures: []
        });
        return;
      }

      const result = response.data;
      console.log('Group Company Details API Response:', result);

      if (result.statusCode === 200 && result.data) {
        setGroupCompanyDetails(result.data);
        console.log(`Loaded group company details:`, result.data);
      } else {
        console.log('Invalid group company details response structure:', result);
        setGroupCompanyDetails({
          associates: [],
          subsidiaries: [],
          joint_ventures: []
        });
      }
    } catch (error) {
      console.error('Error fetching group company details:', error);
      setGroupCompanyDetails({
        associates: [],
        subsidiaries: [],
        joint_ventures: []
      });
    } finally {
      setGroupCompanyLoading(false);
    }
  };

  // Fetch ratings data
  const fetchRatingsData = async (issuerName) => {
    setRatingsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/ratings/issuer/${encodeURIComponent(issuerName)}`);
      const result = response.data;
      if (result.statusCode === 200 && result.data) {
        const filteredData = {};
        const expectedAgencies = ['care', 'crisil', 'icra', 'india', 'sp', 'fitch'];
        expectedAgencies.forEach(agency => {
          if (result.data[agency] && Array.isArray(result.data[agency]) && result.data[agency].length > 0) {
            filteredData[agency] = result.data[agency];
          }
        });
        setRatingsData(filteredData);
      } else {
        setRatingsData({});
      }
    } catch (error) {
      console.error('Error fetching ratings data:', error);
      setRatingsData({});
    } finally {
      setRatingsLoading(false);
    }
  };

  // Fetch news data
  const fetchNewsData = async (issuerName) => {
    setNewsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/news/issuer/${encodeURIComponent(issuerName)}`);
      const result = response.data;
      if (result.statusCode === 200 && result.data) {
        setNewsData(result.data);
      } else {
        setNewsData({ content: [], totalElements: 0 });
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setNewsData({ content: [], totalElements: 0 });
    } finally {
      setNewsLoading(false);
    }
  };

  const getAnnouncementsApiBaseUrl = () =>
    baseUrl.includes('/api/v1/sb') ? baseUrl : `${baseUrl}/api/v1/sb`;

  const fetchAnnouncementsPreview = async (issuerName) => {
    setAnnouncementsLoading(true);
    try {
      const apiBaseUrl = getAnnouncementsApiBaseUrl();
      const response = await apiClient.get(
        `${apiBaseUrl}/master-issuer/${encodeURIComponent(issuerName)}/announcements/all`,
        { params: { page: 0, size: 4 }, validateStatus: () => true }
      );

      if (response.status >= 200 && response.status < 300 && response.data?.data) {
        const data = response.data.data;
        setAnnouncementsPreview(data.content || data.announcements || []);
        setTotalAnnouncements(data.totalElements ?? data.totalAnnouncements ?? 0);
      } else {
        setAnnouncementsPreview([]);
        setTotalAnnouncements(0);
      }
    } catch (error) {
      console.error('Error fetching announcements preview:', error);
      setAnnouncementsPreview([]);
      setTotalAnnouncements(0);
    } finally {
      setAnnouncementsLoading(false);
    }
  };

  const fetchModalAnnouncements = async (issuerName, type, page = 1, size = 10) => {
    setModalAnnouncementsLoading(true);
    try {
      const apiBaseUrl = getAnnouncementsApiBaseUrl();
      const response = await apiClient.get(
        `${apiBaseUrl}/master-issuer/${encodeURIComponent(issuerName)}/announcements`,
        { params: { type, page: page - 1, size }, validateStatus: () => true }
      );

      if (response.status >= 200 && response.status < 300 && response.data?.data) {
        const data = response.data.data;
        setModalAnnouncements(data.content || data.announcements || []);
        setModalAnnouncementsTotal(data.totalElements ?? data.totalAnnouncements ?? 0);
      } else {
        setModalAnnouncements([]);
        setModalAnnouncementsTotal(0);
      }
    } catch (error) {
      console.error('Error fetching modal announcements:', error);
      setModalAnnouncements([]);
      setModalAnnouncementsTotal(0);
    } finally {
      setModalAnnouncementsLoading(false);
    }
  };

  const handleOpenAnnouncementsModal = () => {
    setAnnouncementModalTab('FINANCIAL_ISIN');
    setModalAnnouncementsPage(1);
    setModalAnnouncementsPageSize(10);
    openModal('announcements');
    if (issuerData?.issuer) {
      fetchModalAnnouncements(issuerData.issuer, 'FINANCIAL_ISIN', 1, 10);
    }
  };

  const handleAnnouncementModalTabChange = (key) => {
    setAnnouncementModalTab(key);
    setModalAnnouncementsPage(1);
    if (issuerData?.issuer) {
      fetchModalAnnouncements(issuerData.issuer, key, 1, modalAnnouncementsPageSize);
    }
  };

  const handleAnnouncementTableChange = (pagination) => {
    const nextPage = pagination.current;
    const nextSize = pagination.pageSize;
    setModalAnnouncementsPage(nextPage);
    setModalAnnouncementsPageSize(nextSize);
    if (issuerData?.issuer) {
      fetchModalAnnouncements(issuerData.issuer, announcementModalTab, nextPage, nextSize);
    }
  };

  // Fetch promoter details grouped by category
  const fetchPromoterDetails = async (issuerName) => {
    setPromoterDetailsLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/shareholding-pattern/promoter-details/${encodeURIComponent(issuerName)}`);
      const result = response.data;
      if (result.statusCode === 200 && result.data && typeof result.data === 'object') {
        setPromoterDetailsData(result.data);
      } else {
        setPromoterDetailsData({});
      }
    } catch (error) {
      console.error('Error fetching promoter details:', error);
      setPromoterDetailsData({});
    } finally {
      setPromoterDetailsLoading(false);
    }
  };

  const fetchPromoterScore = async (issuerName) => {
    setPromoterScoreLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/shareholding-pattern/shareholding-promoter/score/${encodeURIComponent(issuerName)}`);
      const result = response.data;
      if (response.status === 200) {
        setPromoterScoreData(result.data);
      }
    } catch (error) {
      console.error('Error fetching promoter score:', error);
    } finally {
      setPromoterScoreLoading(false);
    }
  };

  // Fetch financials when issuer data is loaded
  useEffect(() => {
    if (issuerData?.issuer) {
      fetchIssuerFinancialReports(issuerData.issuer);
      fetchGroupCompanyDetails(issuerData.issuer);
      fetchRatingsData(issuerData.issuer);
      fetchPromoterDetails(issuerData.issuer);
      fetchPromoterScore(issuerData.issuer);
      fetchNewsData(issuerData.issuer);
      fetchAnnouncementsPreview(issuerData.issuer);
      fetchFixedDepositRates(issuerData.issuer);
      fetchIssuerBorrowings(issuerData.issuer);
    }
  }, [issuerData]);







  // Refresh data function
  const handleRefresh = () => {
    fetchIssuerData();
    fetchIsinsData();
  };

  // Handle document download (placeholder for real implementation)
  const handleDocumentDownload = (document) => {
    message.info(`Downloading ${document.name}...`);
    // Implement actual download logic here
    console.log('Downloading document:', document);
  };



  const getTruncatedText = (text, maxLength = 20) => {
    if (!text) return 'N/A';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const formatBorrowingValue = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') {
      return value.toLocaleString('en-IN', { maximumFractionDigits: 2 });
    }
    return value;
  };

  const borrowingFieldDefinitions = [
    { key: 'termLoanFromBanks', label: 'Term Loan From Banks' },
    { key: 'termLoanFromOtherBanks', label: 'Term Loan From Other Banks' },
    { key: 'termLoanFromFinancialInstitutions', label: 'Term Loan From Financial Institutions' },
    { key: 'termLoanFromForeignBanks', label: 'Term Loan From Foreign Banks' },
    { key: 'termLoanFromForeignFinancialInstitutions', label: 'Term Loan From Foreign Financial Institutions' },
    { key: 'externalCommercialBorrowingsEcb', label: 'External Commercial Borrowings (ECB)' },
    { key: 'otherLoans', label: 'Other Loans' },
    { key: 'termLoanFromNhb', label: 'Term Loan From NHB' },
    { key: 'nonConvertibleDebenturesNcds', label: 'Non-Convertible Debentures (NCDs)' },
    { key: 'loansRepayableOnDemandWorkingCapitalDemandLoan', label: 'Loans Repayable on Demand (Working Capital/Demand Loan)' },
    { key: 'loansFromRelatedParties', label: 'Loans From Related Parties' },
    { key: 'cashCreditFromBanks', label: 'Cash Credit From Banks' },
    { key: 'bankOverdraft', label: 'Bank Overdraft' },
    { key: 'borrowingsWithinIndia', label: 'Borrowings Within India' },
    { key: 'borrowingsOutsideIndia', label: 'Borrowings Outside India' }
  ];

  const renderBorrowingFields = (borrowing) => {
    const visibleFields = borrowingFieldDefinitions
      .map((field) => ({
        ...field,
        value: formatBorrowingValue(borrowing?.[field.key])
      }))
      .filter((field) => field.value !== null);

    if (visibleFields.length === 0) {
      return <Text type="secondary">No borrowing values available</Text>;
    }

    return visibleFields.map((field) => (
      <div key={field.key} className="borrowing-field">
        <span className="borrowing-field-label">{field.label}</span>
        <span className="borrowing-field-value">{field.value}</span>
      </div>
    ));
  };

  const formatArrayValue = (value) => {
    if (!value) return 'N/A';
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
      } catch (e) {
        // If parsing fails, return as is
      }
    }
    return value;
  };

  const getBorrowingSecurityType = (borrowing) => {
    return borrowing?.borrowingSecurityType || borrowing?.id?.borrowingSecurityType || 'UNKNOWN';
  };

  const issuerBorrowingsSafe = Array.isArray(issuerBorrowings) ? issuerBorrowings : [];
  const securedBorrowings = issuerBorrowingsSafe.filter(
    borrowing => getBorrowingSecurityType(borrowing) === 'SECURED'
  );
  const unsecuredBorrowings = issuerBorrowingsSafe.filter(
    borrowing => getBorrowingSecurityType(borrowing) === 'UNSECURED'
  );

  const handleIsinClick = (isin) => {
    // Check if we're in dashboard context
    const isInDashboard = window.location.pathname.includes('/d/');
    if (isInDashboard) {
      navigate(`/d/isin-details/${isin.isin}`);
    } else {
      navigate(`/isin-details/${isin.isin}`);
    }
  };

  const getRatingColor = (rating) => {
    const ratingColors = {
      'AAA': '#52c41a',
      'AA': '#1890ff',
      'A': '#722ed1',
      'BBB': '#faad14',
      'BB': '#fa8c16',
      'B': '#f5222d'
    };
    return ratingColors[rating] || '#d9d9d9';
  };

  // Helper functions for ratings
  const getHighestRating = (ratings) => {
    if (!ratings || ratings.length === 0) return null;
    const validRatings = ratings.filter(rating => {
      const ratingValue = rating.id?.rating || rating.rating || '';
      return !ratingValue.toUpperCase().includes('WD') &&
        !ratingValue.toUpperCase().includes('WITHDRAWN');
    });
    if (validRatings.length === 0) return ratings[0];
    const sortedRatings = validRatings.sort((a, b) => {
      const ratingA = a.id?.rating || a.rating || '';
      const ratingB = b.id?.rating || b.rating || '';
      return ratingA.localeCompare(ratingB);
    });
    return sortedRatings[0];
  };

  const cleanRatingText = (rating) => {
    if (!rating) return '';
    return rating.toString()
      .replace(/;\s*Stable/gi, '')
      .replace(/;\s*Positive/gi, '')
      .replace(/;\s*Negative/gi, '')
      .replace(/;\s*Watch/gi, '')
      .replace(/;\s*Outlook/gi, '')
      .trim();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  const formatPromoterCategory = (category) => {
    if (!category) return 'Unknown';
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Modal handlers
  const openModal = (modalType) => {
    setModalVisible(prev => ({ ...prev, [modalType]: true }));
  };

  const closeModal = (modalType) => {
    setModalVisible(prev => ({ ...prev, [modalType]: false }));
  };

  const announcementTableColumns = [
    {
      title: 'Subject',
      dataIndex: 'description',
      key: 'description',
      width: 280,
      render: (description) => (
        <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {description || 'No subject'}
        </Paragraph>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'announcementType',
      key: 'announcementType',
      width: 160,
      render: (_, record) => {
        const type = getAnnouncementTypeKey(record);
        return (
          <Tag color={ANNOUNCEMENT_TYPE_COLORS[type] || 'default'}>
            {ANNOUNCEMENT_TYPE_LABELS[type] || type || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title: 'Details',
      dataIndex: 'attachmentText',
      key: 'attachmentText',
      render: (attachmentText) => (
        <Paragraph
          style={{ marginBottom: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          ellipsis={{ rows: 2, expandable: true, symbol: 'more' }}
        >
          {attachmentText || 'No details available'}
        </Paragraph>
      ),
    },
    {
      title: 'Attachment',
      key: 'attachment',
      width: 130,
      align: 'center',
      render: (_, record) =>
        record.attachmentFile ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            href={record.attachmentFile}
            target="_blank"
            rel="noopener noreferrer"
          >
            View PDF
          </Button>
        ) : (
          <Text type="secondary">No file</Text>
        ),
    },
    {
      title: 'Date',
      dataIndex: 'announcementDate',
      key: 'announcementDate',
      width: 160,
      render: (date) => (
        <Space size="small">
          <CalendarOutlined style={{ color: '#163300' }} />
          <Text>{date || 'N/A'}</Text>
        </Space>
      ),
    },
  ];

  const columns = [
    {
      title: 'Document Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (text) => <Text type="secondary">{text}</Text>
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<DownloadOutlined />}
          onClick={() => handleDocumentDownload(record)}
        >
          Download
        </Button>
      )
    }
  ];

  const loadingContent = (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <Spin size="large" />
      <div style={{ marginTop: '20px' }}>Loading issuer details...</div>
    </div>
  );

  if (loading) {
    return skipLayout ? loadingContent : <LandingLayout>{loadingContent}</LandingLayout>;
  }

  const errorContent = (
    <div style={{ padding: '20px' }}>
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        action={
          <Button size="small" danger onClick={fetchIssuerData}>
            Retry
          </Button>
        }
      />
    </div>
  );

  if (!issuerId) {
    const noIssuerIdContent = (
      <div style={{ padding: '20px' }}>
        <Alert
          message="Missing Issuer ID"
          description="No issuer ID provided. Please go back and select an issuer."
          type="error"
          showIcon
          action={
            <Button size="small" onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </div>
    );
    return skipLayout ? noIssuerIdContent : <LandingLayout>{noIssuerIdContent}</LandingLayout>;
  }

  if (error) {
    return skipLayout ? errorContent : <LandingLayout>{errorContent}</LandingLayout>;
  }

  const sortedAnnualReports = sortIssuerFinancialReportsDesc(issuerAnnualReports);
  const sortedQuarterlyReports = sortIssuerFinancialReportsDesc(issuerQuarterlyReports);
  const latestAnnualReport = sortedAnnualReports[0];
  const latestQuarterlyReport = sortedQuarterlyReports[0];
  const issuerLogoUrl = getIssuerLogoUrl(issuerData);

  const openFinancialStatement = (financial) => {
    if (!financial) return;
    const isDashboardPath = typeof window !== 'undefined' && window.location.pathname.includes('/d/');
    const financialRoute = isDashboardPath ? '/d/financial-details' : '/financial-details';
    navigate(financialRoute, {
      state: {
        data: financial,
        basicIndustry:
          financialIssuerData?.basicIndustry ||
          issuerData?.classification?.basicIndustry ||
          issuerData?.basicIndustry,
        issuerName: issuerData?.issuer,
        issuer: {
          issuer: issuerData?.issuer,
          basicIndustry:
            financialIssuerData?.basicIndustry ||
            issuerData?.classification?.basicIndustry ||
            issuerData?.basicIndustry,
        },
        annualReports: issuerAnnualReports,
        quarterlyReports: issuerQuarterlyReports,
      },
    });
  };

  const content = (
    <div className={`issuer-detail-container issuer-detail-page ${isDark ? 'dark' : ''}`}>
      {/* Fintech header: breadcrumb + title + actions (same as ISIN details) */}
      <header className="issuer-detail-header">
        <div className="issuer-detail-header-inner">
          <nav className="issuer-detail-breadcrumb" aria-label="Breadcrumb">
            <button type="button" className="issuer-detail-breadcrumb-link" onClick={() => navigate('/issuer-categorization')}>
              Issuers
            </button>
            <span className="issuer-detail-breadcrumb-sep" aria-hidden>›</span>
            <span className="issuer-detail-breadcrumb-current">{issuerData.issuer || 'Details'}</span>
          </nav>
          <div className="issuer-detail-title-row">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="issuer-detail-btn-back"
            >
              Back
            </Button>
            <div className="issuer-detail-title-brand">
              <div className="issuer-detail-logo" aria-hidden={!issuerLogoUrl}>
                {issuerLogoUrl ? (
                  <img
                    src={issuerLogoUrl}
                    alt={`${issuerData.issuer || 'Issuer'} logo`}
                    className="issuer-detail-logo-img"
                  />
                ) : (
                  <span className="issuer-detail-logo-initials">
                    {getIssuerInitials(issuerData.issuer || '')}
                  </span>
                )}
              </div>
              <h1 className="issuer-detail-title">{issuerData.issuer}</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Key metrics strip + View details (same pattern as ISIN details) */}
      <div className="issuer-detail-metrics">
        <div className="issuer-detail-metrics-inner">
          {issuerData.marketcap != null && issuerData.marketcap !== '' && (
            <>
              <div className="issuer-detail-metric">
                <span className="issuer-detail-metric-label">Equity Market Cap</span>
                <span className="issuer-detail-metric-value issuer-detail-metric-accent">
                  {formatMarketCapInCr(issuerData.marketcap) ?? String(issuerData.marketcap)}
                </span>
              </div>
              <div className="issuer-detail-metric-divider" aria-hidden />
            </>
          )}
          <div className="issuer-detail-metric">
            <span className="issuer-detail-metric-label">Total Bond Borrowings</span>
            <button
              type="button"
              className="issuer-detail-metric-value issuer-detail-metric-action"
              onClick={handleBondBorrowingsClick}
              title="View ISIN-wise breakdown"
            >
              {issuerData.totalBondBorrowings ? (issuerData.totalBondBorrowings).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr' : 'N/A'}
            </button>
            <button type="button" className="issuer-detail-metric-hint" onClick={handleBondBorrowingsClick}>
              View breakdown
            </button>
          </div>
          <div className="issuer-detail-metric-divider" aria-hidden />
          <div className="issuer-detail-metric">
            <span className="issuer-detail-metric-label">Basic Industry</span>
            <button
              type="button"
              className="issuer-detail-metric-value issuer-detail-metric-action"
              onClick={handleBasicIndustryClick}
              title="View peer issuers in this industry"
            >
              {issuerData.classification ? formatClassificationValue(issuerData.classification.basicIndustry) : 'N/A'}
            </button>
            <button type="button" className="issuer-detail-metric-hint" onClick={handleBasicIndustryClick}>
              View peers
            </button>
          </div>
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openModal('overview')}
            className="issuer-detail-metrics-view-btn"
          >
            View details
          </Button>
        </div>
      </div>

      <div className="issuer-detail-content">
        <Modal
          title={
            <span>
              <ExpandOutlined style={{ marginRight: 8 }} />
              Default risk trend — full view
            </span>
          }
          open={pdChartFullscreenOpen}
          onCancel={() => setPdChartFullscreenOpen(false)}
          footer={null}
          width="100%"
          style={{ top: 0, paddingBottom: 0, maxWidth: '100vw' }}
          styles={{
            body: {
              padding: 16,
              minHeight: 'calc(100vh - 120px)',
            },
          }}
          destroyOnClose
        >
          {pdSeries.length > 0 ? (
            <div style={{ width: '100%', height: 'min(70vh, 720px)', minHeight: 400, overflow: 'visible' }}>
              <LineChart
                xAxis={[
                  {
                    scaleType: 'point',
                    data: pdChartX,
                    height: 72,
                    tickLabelInterval: pdChartTickLabelInterval,
                    tickLabelStyle: {
                      fontSize: 11,
                      angle: -35,
                      fill: isDark ? '#cbd5e1' : '#475569',
                    },
                  },
                ]}
                yAxis={[
                  {
                    min: 0,
                    tickLabelStyle: { fill: isDark ? '#cbd5e1' : '#475569' },
                  },
                ]}
                series={[
                  {
                    data: pdChartY,
                    label: 'Default risk (%)',
                    color: '#25528d',
                    area: true,
                    showMark: false,
                    curve: 'monotoneX',
                  },
                ]}
                grid={{ horizontal: true, vertical: false }}
                height={pdChartFullscreenHeight}
                margin={{ left: 48, right: 24, top: 16, bottom: 16 }}
                sx={{
                  '& .MuiChartsAxis-line': { stroke: isDark ? 'rgba(148, 163, 184, 0.35)' : 'rgba(100, 116, 139, 0.35)' },
                  '& .MuiChartsGrid-line': { stroke: isDark ? 'rgba(148, 163, 184, 0.15)' : 'rgba(100, 116, 139, 0.15)' },
                }}
              />
            </div>
          ) : (
            <Text type="secondary">No historical default risk data available</Text>
          )}
        </Modal>
        <Row gutter={[24, 24]}>
              {/* ISINs Section */}
              <Col xs={24} lg={18}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <BankOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>ISINs</Title>
                        <Tag color="blue">{isinsData.length}</Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('isins')}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {isinsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : isinsData.length > 0 ? (
                    <Row gutter={[16, 16]} className="issuer-detail-isin-cards">
                      {isinsData.slice(0, 2).map((isin, index) => (
                        <Col
                          xs={24}
                          sm={12}
                          lg={12}
                          key={isin.isin || index}
                        >
                          <ISINCard
                            isin={isin}
                            index={index}
                            onViewDetails={handleIsinClick}
                          />
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No ISINs found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Ratings Section */}
              <Col xs={24} lg={6}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <TrophyOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Ratings</Title>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/issuer-ratings/${encodeURIComponent(issuerData?.issuer || '')}`)}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {ratingsLoading ? (
                    <div className="issuer-detail-ratings-loading">
                      <Spin size="large" />
                    </div>
                  ) : ratingsData && Object.keys(ratingsData).length > 0 ? (
                    (() => {
                      const agencies = Object.keys(ratingsData);
                      const rows = agencies
                        .map(agency => {
                          const highest = getHighestRating(ratingsData[agency]);
                          if (!highest) return null;
                          const rating = highest.id?.rating || highest.rating;
                          const raw = cleanRatingText(rating);
                          const cleaned = raw ? raw.replace(/\s+/g, ' ').trim() : '—';
                          const agencyDisplay = agency === 'india' ? 'INDIA' : agency.toUpperCase();
                          return { key: agency, rating: cleaned, agency: agencyDisplay };
                        })
                        .filter(Boolean);
                      const previewCount = 4;
                      const displayRows = rows.slice(0, previewCount);
                      return (
                        <div className="issuer-detail-ratings-list">
                          <div className="issuer-detail-ratings-header">
                            <span className="issuer-detail-ratings-header-rating">Rating</span>
                            <span className="issuer-detail-ratings-header-agency">Agency</span>
                          </div>
                          {displayRows.map(({ key, rating, agency }) => (
                            <div key={key} className="issuer-detail-rating-row">
                              <span className="issuer-detail-rating-value">{rating}</span>
                              <span className="issuer-detail-rating-agency">{agency}</span>
                            </div>
                          ))}
                          {rows.length > previewCount && (
                            <div className="issuer-detail-ratings-more">
                              +{rows.length - previewCount} more ratings — see View More
                            </div>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div className="issuer-detail-ratings-empty">
                      <Text type="secondary">No ratings found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Default risk predictor */}
              <Col xs={24} lg={24}>
                <DefaultRiskPredictor
                  data={defaultRiskData}
                  loading={latestMertonLoading || mertonHistoryLoading}
                  onChartClick={
                    mertonPdHistory.length > 0 && !mertonHistoryLoading
                      ? () => setPdChartFullscreenOpen(true)
                      : undefined
                  }
                />
              </Col>

              {/* Source Code Number Section (formerly Promotor Details) */}
              <Col xs={24} lg={24}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <UserOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Composite Goverence Score(SCN)</Title>
                      </span>
                    </div>
                  }
                >
                  {promoterScoreLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <div className="issuer-governance-layout">
                      {promoterScoreData ? (
                        <Row gutter={[24, 24]} align="stretch">
                          <Col xs={24} md={10} lg={9}>
                            <div
                              className={`issuer-governance-score-panel${isDark ? ' issuer-governance-score-panel--dark' : ''}`}
                            >
                              <Text strong className="issuer-governance-score-heading">
                                Composite Governance Score
                              </Text>
                              <div className="issuer-governance-circle-wrap">
                                <Progress
                                  type="circle"
                                  className="issuer-governance-progress-circle"
                                  percent={clampPercentDisplay(promoterScoreData.score)}
                                  width={180}
                                  strokeWidth={12}
                                  trailColor={isDark ? '#36384b' : '#f1f5f9'}
                                  strokeColor={{
                                    '0%': '#9fe870',
                                    '100%': '#163300',
                                  }}
                                  format={() => (
                                    <div className="issuer-governance-circle-inner">
                                      <span className="issuer-governance-circle-score">
                                        {clampPercentDisplay(promoterScoreData.score)}
                                      </span>
                                      <span
                                        className={`issuer-governance-circle-unit${isDark ? ' issuer-governance-circle-unit--dark' : ''}`}
                                      >
                                        POINTS
                                      </span>
                                    </div>
                                  )}
                                />
                              </div>
                              {promoterScoreData.promoterScore != null &&
                                promoterScoreData.promoterScore !== '' && (
                                <Tag className="issuer-governance-weighted-tag" color="processing">
                                  Weighted score: {Number(promoterScoreData.promoterScore).toFixed(2)}
                                </Tag>
                              )}
                            </div>
                          </Col>
                          <Col xs={24} md={14} lg={15}>
                            <div
                              className={`issuer-governance-factors${isDark ? ' issuer-governance-factors--dark' : ''}`}
                            >
                              <Text strong className="issuer-governance-factors-title">
                                Factors for Composite Governance Score
                              </Text>
                              <div className="issuer-governance-factors-chart">
                                {[
                                  { label: 'Corporate Governance', value: promoterScoreData.governanceScore },
                                  { label: 'Promoter Quality', value: promoterScoreData.promoterScore },
                                ].map((item, index) => {
                                  const scoreValue = Number(item.value) || 0;
                                  const heightPercent = Math.max(scoreValue, 5);
                                  return (
                                    <div key={index} className="issuer-governance-factor-col">
                                      <Text strong className="issuer-governance-factor-value">
                                        {scoreValue.toFixed(2)}
                                      </Text>
                                      <div
                                        className="issuer-governance-factor-bar"
                                        style={{
                                          height: `${heightPercent}%`,
                                          background:
                                            scoreValue > 75
                                              ? '#52c41a'
                                              : scoreValue > 50
                                                ? '#faad14'
                                                : '#f5222d',
                                        }}
                                      />
                                      <div className="issuer-governance-factor-label">{item.label}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </Col>
                        </Row>
                      ) : (
                        <div className="issuer-governance-empty">
                          <Text type="secondary">No score data available</Text>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              </Col>

              {/* Financials Section */}
              <Col xs={24} lg={12}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <FileTextOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Financials</Title>
                        <Tag color="green">
                          {issuerAnnualReports.length + issuerQuarterlyReports.length}
                        </Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('financials')}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {issuerFinancialsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : issuerAnnualReports.length > 0 || issuerQuarterlyReports.length > 0 ? (
                    <div className="issuer-financial-preview">
                      <Text type="secondary" className="issuer-financial-preview-footnote">
                        Latest annual and quarterly snapshots (newest period first). Tap a row for the full statement.
                      </Text>
                      <div className="issuer-financial-preview-blocks">
                        {latestAnnualReport && (
                          <div
                            role="button"
                            tabIndex={0}
                            className="issuer-financial-preview-block"
                            onClick={() => openFinancialStatement(latestAnnualReport)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openFinancialStatement(latestAnnualReport);
                              }
                            }}
                          >
                            <div className="issuer-financial-preview-block-heading">
                              <Text strong className="issuer-financial-preview-block-title">
                                Annual
                              </Text>
                              <Tag color="geekblue">Year</Tag>
                            </div>
                            <div className="issuer-financial-preview-block-meta">
                              <Text type="secondary">
                                {latestAnnualReport.periodType || 'Year ended'} · {latestAnnualReport.endDate || '—'}
                              </Text>
                              {latestAnnualReport.id?.statementType && (
                                <Tag>{latestAnnualReport.id.statementType}</Tag>
                              )}
                              {(() => {
                                const a = getAuditBadgeFromColumnUsed(
                                  latestAnnualReport.columnUsed,
                                  latestAnnualReport.auditStatus
                                );
                                return (
                                  <Tag color={a.color} title={latestAnnualReport.columnUsed || ''}>
                                    {a.shortLabel}
                                  </Tag>
                                );
                              })()}
                            </div>
                            <div className="issuer-financial-preview-metrics">
                              {getFinancialHighlightMetrics(latestAnnualReport).map(({ label, value }) => (
                                <div key={label} className="issuer-financial-preview-metric">
                                  <span className="issuer-financial-preview-metric-label">{label}</span>
                                  <span className="issuer-financial-preview-metric-value">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {latestQuarterlyReport && (
                          <div
                            role="button"
                            tabIndex={0}
                            className="issuer-financial-preview-block"
                            onClick={() => openFinancialStatement(latestQuarterlyReport)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                openFinancialStatement(latestQuarterlyReport);
                              }
                            }}
                          >
                            <div className="issuer-financial-preview-block-heading">
                              <Text strong className="issuer-financial-preview-block-title">
                                Quarterly
                              </Text>
                              <Tag color="cyan">Quarter</Tag>
                            </div>
                            <div className="issuer-financial-preview-block-meta">
                              <Text type="secondary">
                                {latestQuarterlyReport.periodType || 'Quarter ended'} ·{' '}
                                {latestQuarterlyReport.endDate || '—'}
                              </Text>
                              {latestQuarterlyReport.id?.statementType && (
                                <Tag>{latestQuarterlyReport.id.statementType}</Tag>
                              )}
                              {(() => {
                                const a = getAuditBadgeFromColumnUsed(
                                  latestQuarterlyReport.columnUsed,
                                  latestQuarterlyReport.auditStatus
                                );
                                return (
                                  <Tag color={a.color} title={latestQuarterlyReport.columnUsed || ''}>
                                    {a.shortLabel}
                                  </Tag>
                                );
                              })()}
                              {latestQuarterlyReport.id?.reportingQuarter && (
                                <Text type="secondary" style={{ fontSize: 11 }}>
                                  {latestQuarterlyReport.id.reportingQuarter}
                                </Text>
                              )}
                            </div>
                            <div className="issuer-financial-preview-metrics">
                              {getFinancialHighlightMetrics(latestQuarterlyReport).map(({ label, value }) => (
                                <div key={`q-${label}`} className="issuer-financial-preview-metric">
                                  <span className="issuer-financial-preview-metric-label">{label}</span>
                                  <span className="issuer-financial-preview-metric-value">{value}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No financials found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* News Section */}
              <Col xs={24} lg={12}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <FileTextOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>News</Title>
                        <Tag color="purple">{newsData?.totalElements || 0}</Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('news')}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {newsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : newsData?.content && newsData.content.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {newsData.content.slice(0, 2).map((news, index) => (
                        <div key={news.id || index} style={{
                          padding: '12px',
                          background: '#f8fafc',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                          onClick={() => {
                            navigate(`/news-details/${encodeURIComponent(issuerData?.issuer || '')}/${encodeURIComponent(news.id || news.title)}`, {
                              state: { news, issuerName: issuerData?.issuer }
                            });
                          }}
                        >
                          <Text strong style={{ display: 'block', marginBottom: '4px' }}>
                            {getTruncatedText(news.title || 'N/A', 60)}
                          </Text>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                            <Tag color="blue" style={{ fontSize: '11px' }}>{news.source || 'Unknown'}</Tag>
                            <Text type="secondary" style={{ fontSize: '11px' }}>
                              <CalendarOutlined /> {formatDate(news.publishedDate)}
                            </Text>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No news found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Group Companies Section */}
              <Col xs={24} lg={24}>
                <Card
                  className="modern-section-card"
                  title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <GlobalOutlined style={{ color: '#fa8c16' }} />
                        <Title level={4} style={{ margin: 0 }}>Group Companies</Title>
                        <Tag color="orange">
                          {groupCompanyDetails ?
                            (groupCompanyDetails.associates?.length || 0) +
                            (groupCompanyDetails.subsidiaries?.length || 0) +
                            (groupCompanyDetails.joint_ventures?.length || 0)
                            : 0}
                        </Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => openModal('groupCompanies')}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {groupCompanyLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : groupCompanyDetails ? (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={8}>
                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <Tag color="blue" style={{ marginBottom: '8px' }}>
                            Subsidiaries ({groupCompanyDetails.subsidiaries?.length || 0})
                          </Tag>
                          {groupCompanyDetails.subsidiaries && groupCompanyDetails.subsidiaries.length > 0 ? (
                            <div style={{ marginTop: '8px' }}>
                              {groupCompanyDetails.subsidiaries.slice(0, 3).map((company, idx) => (
                                <div key={idx} style={{ padding: '4px 0', fontSize: '13px' }}>
                                  {getTruncatedText(company, 40)}
                                </div>
                              ))}
                              {groupCompanyDetails.subsidiaries.length > 3 && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  +{groupCompanyDetails.subsidiaries.length - 3} more
                                </Text>
                              )}
                            </div>
                          ) : (
                            <Text type="secondary" style={{ fontSize: '12px' }}>None</Text>
                          )}
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <Tag color="green" style={{ marginBottom: '8px' }}>
                            Associates ({groupCompanyDetails.associates?.length || 0})
                          </Tag>
                          {groupCompanyDetails.associates && groupCompanyDetails.associates.length > 0 ? (
                            <div style={{ marginTop: '8px' }}>
                              {groupCompanyDetails.associates.slice(0, 3).map((company, idx) => (
                                <div key={idx} style={{ padding: '4px 0', fontSize: '13px' }}>
                                  {getTruncatedText(company, 40)}
                                </div>
                              ))}
                              {groupCompanyDetails.associates.length > 3 && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  +{groupCompanyDetails.associates.length - 3} more
                                </Text>
                              )}
                            </div>
                          ) : (
                            <Text type="secondary" style={{ fontSize: '12px' }}>None</Text>
                          )}
                        </div>
                      </Col>
                      <Col xs={24} md={8}>
                        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                          <Tag color="orange" style={{ marginBottom: '8px' }}>
                            Joint Ventures ({groupCompanyDetails.joint_ventures?.length || 0})
                          </Tag>
                          {groupCompanyDetails.joint_ventures && groupCompanyDetails.joint_ventures.length > 0 ? (
                            <div style={{ marginTop: '8px' }}>
                              {groupCompanyDetails.joint_ventures.slice(0, 3).map((company, idx) => (
                                <div key={idx} style={{ padding: '4px 0', fontSize: '13px' }}>
                                  {getTruncatedText(company, 40)}
                                </div>
                              ))}
                              {groupCompanyDetails.joint_ventures.length > 3 && (
                                <Text type="secondary" style={{ fontSize: '12px' }}>
                                  +{groupCompanyDetails.joint_ventures.length - 3} more
                                </Text>
                              )}
                            </div>
                          ) : (
                            <Text type="secondary" style={{ fontSize: '12px' }}>None</Text>
                          )}
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No group company details found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Corporate Announcements Section */}
              <Col xs={24} lg={24}>
                <Card
                  className="issuer-detail-section-card modern-section-card issuer-announcements-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <NotificationOutlined className="issuer-detail-card-icon issuer-announcements-icon" />
                        <Title level={4} style={{ margin: 0 }}>Corporate Announcements</Title>
                        <Tag color="geekblue">{totalAnnouncements}</Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={handleOpenAnnouncementsModal}
                        disabled={!issuerData?.issuer}
                      >
                        View More
                      </Button>
                    </div>
                  }
                >
                  {announcementsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : announcementsPreview.length > 0 ? (
                    <Row gutter={[16, 16]} className="issuer-announcement-preview-list">
                      {announcementsPreview.slice(0, 4).map((announcement, index) => (
                        <Col xs={24} sm={12} key={`${announcement.seqId || getAnnouncementTypeKey(announcement)}-${index}`}>
                          <div className="issuer-announcement-preview-item">
                            <div className="issuer-announcement-preview-item__header">
                              <Text strong className="issuer-announcement-preview-item__subject">
                                {getTruncatedText(announcement.description || 'No subject', 90)}
                              </Text>
                              <Tag
                                color={ANNOUNCEMENT_TYPE_COLORS[getAnnouncementTypeKey(announcement)] || 'default'}
                                className="issuer-announcement-preview-item__type"
                              >
                                {ANNOUNCEMENT_TYPE_LABELS[getAnnouncementTypeKey(announcement)] || getAnnouncementTypeKey(announcement)}
                              </Tag>
                            </div>
                            {announcement.attachmentText && (
                              <Text type="secondary" className="issuer-announcement-preview-item__details">
                                {getTruncatedText(announcement.attachmentText, 120)}
                              </Text>
                            )}
                            <div className="issuer-announcement-preview-item__footer">
                              <Text type="secondary" className="issuer-announcement-preview-item__date">
                                <CalendarOutlined /> {announcement.announcementDate || 'N/A'}
                              </Text>
                              {announcement.attachmentFile ? (
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EyeOutlined />}
                                  href={announcement.attachmentFile}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="issuer-announcement-preview-item__pdf"
                                >
                                  View PDF
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div className="issuer-announcement-empty">
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No corporate announcements found for this issuer"
                      />
                    </div>
                  )}
                </Card>
              </Col>

              {/* Borrowings Section */}
              <Col xs={24} lg={24}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <DollarOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Borrowings</Title>
                        <Tag color="cyan">{issuerBorrowingsSafe.length}</Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => navigate(`/borrowing-details/${encodeURIComponent(issuerData?.issuer || '')}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  }
                >
                  {issuerBorrowingsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : issuerBorrowingsError ? (
                    <Alert message={issuerBorrowingsError} type="error" showIcon />
                  ) : securedBorrowings.length > 0 || unsecuredBorrowings.length > 0 ? (
                    <Row gutter={[16, 16]} className="borrowings-grid">
                      <Col xs={24} md={12}>
                        <div className="borrowings-group">
                          <div className="borrowings-group-header">
                            <Tag color="green">Secured ({securedBorrowings.length})</Tag>
                          </div>
                          {securedBorrowings.length > 0 ? (
                            securedBorrowings.map((borrowing, index) => (
                              <div key={`secured-${index}`} className="borrowing-card">
                                {renderBorrowingFields(borrowing)}
                              </div>
                            ))
                          ) : (
                            <Text type="secondary">No secured borrowings found</Text>
                          )}
                        </div>
                      </Col>
                      <Col xs={24} md={12}>
                        <div className="borrowings-group">
                          <div className="borrowings-group-header">
                            <Tag color="orange">Unsecured ({unsecuredBorrowings.length})</Tag>
                          </div>
                          {unsecuredBorrowings.length > 0 ? (
                            unsecuredBorrowings.map((borrowing, index) => (
                              <div key={`unsecured-${index}`} className="borrowing-card">
                                {renderBorrowingFields(borrowing)}
                              </div>
                            ))
                          ) : (
                            <Text type="secondary">No unsecured borrowings found</Text>
                          )}
                        </div>
                      </Col>
                    </Row>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No borrowings found</Text>
                    </div>
                  )}
                </Card>
              </Col>

              {/* Fixed Deposit Rates Section */}
              <Col xs={24} lg={24}>
                <Card
                  className="issuer-detail-section-card modern-section-card"
                  title={
                    <div className="issuer-detail-card-title">
                      <span className="issuer-detail-card-title-text">
                        <BankOutlined className="issuer-detail-card-icon" />
                        <Title level={4} style={{ margin: 0 }}>Fixed Deposit Rates</Title>
                        <Tag color="cyan">{fixedDepositRates.length}</Tag>
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => fetchFixedDepositRates(issuerData?.issuer)}
                      >
                        Refresh
                      </Button>
                    </div>
                  }
                >
                  {fixedDepositLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <Spin size="large" />
                    </div>
                  ) : fixedDepositError ? (
                    <Alert
                      message="Fixed deposit rates"
                      description={fixedDepositError}
                      type="warning"
                      showIcon
                    />
                  ) : fixedDepositRates.length > 0 ? (
                    <Table
                      size="middle"
                      pagination={false}
                      rowKey={(record, idx) => `${record.companyName || 'issuer'}-${record.tenure || 'tenure'}-${idx}`}
                      dataSource={fixedDepositRates}
                      columns={[
                        {
                          title: 'Tenure',
                          dataIndex: 'tenure',
                          key: 'tenure',
                          render: (value) => value || 'N/A',
                        },
                        {
                          title: 'Interest Rate',
                          dataIndex: 'interestRate',
                          key: 'interestRate',
                          render: (value) => value || '—',
                        },
                        {
                          title: 'Source',
                          dataIndex: 'sourceUrl',
                          key: 'sourceUrl',
                          render: (value) =>
                            value ? (
                              <a href={value} target="_blank" rel="noreferrer">
                                View
                              </a>
                            ) : (
                              '—'
                            ),
                        },
                      ]}
                    />
                  ) : (
                    <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>
                      <Text type="secondary">No fixed deposit rates found for this issuer</Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            {/* Overview modal (View details from metrics strip) */}
            <Modal
              title="Issuer Overview"
              open={modalVisible.overview}
              onCancel={() => closeModal('overview')}
              footer={null}
              width={560}
            >
              <Descriptions
                bordered
                column={1}
                size="small"
                labelStyle={{ fontWeight: 600, width: 200, background: isDark ? '#1f1f2b' : '#f8fafc' }}
                contentStyle={{ wordBreak: 'break-word', background: isDark ? '#14141f' : '#ffffff' }}
              >
                <Descriptions.Item label="Issuer">{issuerData?.issuer || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="CIN">{issuerData?.corporateIdentityNumber || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="LEI">{issuerData?.legalEntityIdentifier || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Equity Market Cap">
                  {issuerData?.marketcap != null ? formatMarketCapInCr(issuerData.marketcap) : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Total Bond Borrowings">
                  {issuerData?.totalBondBorrowings != null ? (issuerData.totalBondBorrowings).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr' : 'N/A'}
                </Descriptions.Item>
                {issuerData?.classification && (
                  <>
                    <Descriptions.Item label="Macroeconomic Sector">
                      {formatClassificationValue(issuerData.classification.macroeconomicSector)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Sector">{formatClassificationValue(issuerData.classification.sector)}</Descriptions.Item>
                    <Descriptions.Item label="Industry">{formatClassificationValue(issuerData.classification.industry)}</Descriptions.Item>
                    <Descriptions.Item label="Basic Industry">
                      {formatClassificationValue(issuerData.classification.basicIndustry)}
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>
            </Modal>

            <Modal
              title="ISINs"
              open={modalVisible.isins}
              onCancel={() => closeModal('isins')}
              footer={null}
              width={1200}
            >
              <div className="isins-section">
                {isinsLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                  </div>
                ) : isinsData.length > 0 ? (
                  <Row gutter={[24, 24]}>
                    {isinsData.map((isin, index) => (
                      <Col xs={24} sm={12} lg={8} key={index}>
                        <ISINCard
                          isin={isin}
                          index={index}
                          onViewDetails={handleIsinClick}
                        />
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
                    <Text type="secondary">No ISINs found</Text>
                  </div>
                )}
              </div>
            </Modal>

            <Modal
              title={
                <span>
                  Financial statements
                  {(issuerAnnualReports.length > 0 || issuerQuarterlyReports.length > 0) && (
                    <Text type="secondary" style={{ fontWeight: 400, marginLeft: 8 }}>
                      ({issuerAnnualReports.length + issuerQuarterlyReports.length} periods on file)
                    </Text>
                  )}
                </span>
              }
              open={modalVisible.financials}
              onCancel={() => closeModal('financials')}
              footer={null}
              width={1200}
              styles={{ body: { paddingTop: 12 } }}
            >
              {issuerFinancialsLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Spin size="large" />
                </div>
              ) : issuerAnnualReports.length === 0 && issuerQuarterlyReports.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Text type="secondary">No financials found</Text>
                </div>
              ) : (
                <div className="issuer-financial-modal-body">
                  <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    Separate tabs for annual vs quarterly filings. Labels match the filing (statement type,
                    audited / unaudited). Select a row to open the detailed statement.
                  </Paragraph>
                  <Tabs
                    defaultActiveKey="annual"
                    items={[
                      {
                        key: 'annual',
                        label: `Annual (${issuerAnnualReports.length})`,
                        children: (
                          <div className="issuer-financial-modal-list">
                            {sortedAnnualReports.length === 0 ? (
                              <Text type="secondary">No annual reports</Text>
                            ) : (
                              sortedAnnualReports.map((financial, index) => {
                                const periodEnd = financial.endDate || financial.periodEnd || '—';
                                const reportingQuarter =
                                  financial.id?.reportingQuarter || financial.reportingQuarter;
                                const periodType = financial.periodType || financial.reportType || 'Year ended';
                                const statementType = financial.id?.statementType;
                                const fyStart = financial.id?.dateOfStartOfFinancialYear;
                                const audit = getAuditBadgeFromColumnUsed(
                                  financial.columnUsed,
                                  financial.auditStatus
                                );
                                return (
                                  <Card
                                    key={`a-${periodEnd}-${index}`}
                                    size="small"
                                    bordered
                                    hoverable
                                    className="issuer-financial-modal-report-card"
                                    onClick={() => openFinancialStatement(financial)}
                                  >
                                    <div className="issuer-financial-modal-report-card__header">
                                      <div>
                                        <Title level={5} style={{ margin: 0 }}>
                                          {periodType} · {periodEnd}
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {fyStart ? `FY from ${fyStart}` : ''}
                                          {fyStart && reportingQuarter ? ' · ' : ''}
                                          {reportingQuarter ? `Reporting quarter tag: ${reportingQuarter}` : ''}
                                        </Text>
                                      </div>
                                      <div className="issuer-financial-modal-report-card__tags">
                                        {statementType && <Tag>{statementType}</Tag>}
                                        <Tag color={audit.color} title={financial.columnUsed || ''}>
                                          {audit.shortLabel}
                                        </Tag>
                                      </div>
                                    </div>
                                    {financial.columnUsed && audit.shortLabel !== financial.columnUsed && (
                                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                                        Column: {financial.columnUsed}
                                      </Text>
                                    )}
                                    <Row gutter={[12, 8]} style={{ marginTop: 14 }}>
                                      {getFinancialHighlightMetrics(financial).map(({ label, value }) => (
                                        <Col xs={12} sm={12} md={6} key={`${financial.endDate}-${label}`}>
                                          <div className="issuer-financial-modal-report-metric">
                                            <span className="issuer-financial-modal-report-metric-label">
                                              {label}
                                            </span>
                                            <span className="issuer-financial-modal-report-metric-value">
                                              {value}
                                            </span>
                                          </div>
                                        </Col>
                                      ))}
                                    </Row>
                                    <div className="issuer-financial-modal-report-card__cta">
                                      <Text type="secondary">Open full statement</Text>
                                      <EyeOutlined />
                                    </div>
                                  </Card>
                                );
                              })
                            )}
                          </div>
                        ),
                      },
                      {
                        key: 'quarterly',
                        label: `Quarterly (${issuerQuarterlyReports.length})`,
                        children: (
                          <div className="issuer-financial-modal-list">
                            {sortedQuarterlyReports.length === 0 ? (
                              <Text type="secondary">No quarterly reports</Text>
                            ) : (
                              sortedQuarterlyReports.map((financial, index) => {
                                const periodEnd = financial.endDate || financial.periodEnd || '—';
                                const reportingQuarter =
                                  financial.id?.reportingQuarter || financial.reportingQuarter;
                                const periodType = financial.periodType || financial.reportType || 'Quarter ended';
                                const statementType = financial.id?.statementType;
                                const fyStart = financial.id?.dateOfStartOfFinancialYear;
                                const audit = getAuditBadgeFromColumnUsed(
                                  financial.columnUsed,
                                  financial.auditStatus
                                );
                                return (
                                  <Card
                                    key={`q-${periodEnd}-${reportingQuarter || ''}-${index}`}
                                    size="small"
                                    bordered
                                    hoverable
                                    className="issuer-financial-modal-report-card"
                                    onClick={() => openFinancialStatement(financial)}
                                  >
                                    <div className="issuer-financial-modal-report-card__header">
                                      <div>
                                        <Title level={5} style={{ margin: 0 }}>
                                          {periodType} · {periodEnd}
                                        </Title>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {fyStart ? `FY from ${fyStart}` : ''}
                                          {fyStart && reportingQuarter ? ' · ' : ''}
                                          {reportingQuarter ? `${reportingQuarter}` : ''}
                                        </Text>
                                      </div>
                                      <div className="issuer-financial-modal-report-card__tags">
                                        {statementType && <Tag>{statementType}</Tag>}
                                        <Tag color={audit.color} title={financial.columnUsed || ''}>
                                          {audit.shortLabel}
                                        </Tag>
                                      </div>
                                    </div>
                                    {financial.columnUsed && audit.shortLabel !== financial.columnUsed && (
                                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 6 }}>
                                        Column: {financial.columnUsed}
                                      </Text>
                                    )}
                                    <Row gutter={[12, 8]} style={{ marginTop: 14 }}>
                                      {getFinancialHighlightMetrics(financial).map(({ label, value }) => (
                                        <Col xs={12} sm={12} md={6} key={`${financial.endDate}-q-${label}`}>
                                          <div className="issuer-financial-modal-report-metric">
                                            <span className="issuer-financial-modal-report-metric-label">
                                              {label}
                                            </span>
                                            <span className="issuer-financial-modal-report-metric-value">
                                              {value}
                                            </span>
                                          </div>
                                        </Col>
                                      ))}
                                    </Row>
                                    <div className="issuer-financial-modal-report-card__cta">
                                      <Text type="secondary">Open full statement</Text>
                                      <EyeOutlined />
                                    </div>
                                  </Card>
                                );
                              })
                            )}
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
            </Modal>

            <Modal
              title="News"
              open={modalVisible.news}
              onCancel={() => closeModal('news')}
              footer={null}
              width={1000}
            >
              <IssuerNews issuerName={issuerData?.issuer || "CANARA BANK"} />
            </Modal>

            <Modal
              title="Group Companies"
              open={modalVisible.groupCompanies}
              onCancel={() => closeModal('groupCompanies')}
              footer={null}
              width={1000}
            >
              <div className="group-companies-section">
                {groupCompanyLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                  </div>
                ) : groupCompanyDetails ? (
                  <Row gutter={[24, 24]}>
                    <Col xs={24} md={8}>
                      <Card title={<span><Tag color="blue">Subsidiaries</Tag> ({groupCompanyDetails.subsidiaries?.length || 0})</span>}>
                        {groupCompanyDetails.subsidiaries && groupCompanyDetails.subsidiaries.length > 0 ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {groupCompanyDetails.subsidiaries.map((company, index) => (
                              <li key={index} style={{ padding: '8px 0', borderBottom: index < groupCompanyDetails.subsidiaries.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                {company}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text type="secondary">No subsidiaries found</Text>
                        )}
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card title={<span><Tag color="green">Associates</Tag> ({groupCompanyDetails.associates?.length || 0})</span>}>
                        {groupCompanyDetails.associates && groupCompanyDetails.associates.length > 0 ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {groupCompanyDetails.associates.map((company, index) => (
                              <li key={index} style={{ padding: '8px 0', borderBottom: index < groupCompanyDetails.associates.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                {company}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text type="secondary">No associates found</Text>
                        )}
                      </Card>
                    </Col>
                    <Col xs={24} md={8}>
                      <Card title={<span><Tag color="orange">Joint Ventures</Tag> ({groupCompanyDetails.joint_ventures?.length || 0})</span>}>
                        {groupCompanyDetails.joint_ventures && groupCompanyDetails.joint_ventures.length > 0 ? (
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {groupCompanyDetails.joint_ventures.map((company, index) => (
                              <li key={index} style={{ padding: '8px 0', borderBottom: index < groupCompanyDetails.joint_ventures.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                                {company}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <Text type="secondary">No joint ventures found</Text>
                        )}
                      </Card>
                    </Col>
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Text type="secondary">No group company details found</Text>
                  </div>
                )}
              </div>
            </Modal>

            <Modal
              title={
                <Space>
                  <NotificationOutlined />
                  <span>Corporate Announcements — {issuerData?.issuer || ''}</span>
                </Space>
              }
              open={modalVisible.announcements}
              onCancel={() => closeModal('announcements')}
              footer={null}
              width={1100}
              className="issuer-announcements-modal"
              destroyOnClose
            >
              <Tabs
                activeKey={announcementModalTab}
                onChange={handleAnnouncementModalTabChange}
                size="large"
                className="issuer-announcements-modal-tabs"
                items={[
                  {
                    key: 'FINANCIAL_ISIN',
                    label: (
                      <span>
                        <BankOutlined /> Financial ISIN
                      </span>
                    ),
                  },
                  {
                    key: 'FINANCIAL_ISSUER',
                    label: (
                      <span>
                        <CheckCircleOutlined /> Financial Issuer
                      </span>
                    ),
                  },
                  {
                    key: 'NON_FINANCIAL_BUSINESS',
                    label: (
                      <span>
                        <RiseOutlined /> Non-Financial Business
                      </span>
                    ),
                  },
                  {
                    key: 'NON_FINANCIAL_OTHER',
                    label: (
                      <span>
                        <FallOutlined /> Non-Financial Other
                      </span>
                    ),
                  },
                ]}
              />
              <Table
                columns={announcementTableColumns.filter((col) => col.key !== 'announcementType')}
                dataSource={modalAnnouncements}
                rowKey={(record, index) => `${record.seqId || index}-${announcementModalTab}`}
                loading={modalAnnouncementsLoading}
                pagination={{
                  current: modalAnnouncementsPage,
                  pageSize: modalAnnouncementsPageSize,
                  total: modalAnnouncementsTotal,
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} announcements`,
                  pageSizeOptions: ['10', '20', '50'],
                }}
                onChange={handleAnnouncementTableChange}
                scroll={{ x: 960 }}
                locale={{
                  emptyText: (
                    <Empty
                      description={`No ${ANNOUNCEMENT_TYPE_LABELS[announcementModalTab]?.toLowerCase() || ''} announcements found`}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </Modal>

            <Modal
              title={`Peer Group Company`}
              open={modalVisible.basicIndustry}
              onCancel={() => closeModal('basicIndustry')}
              footer={null}
              width={800}
              bodyStyle={{ maxHeight: '500px', overflowY: 'auto' }}
            >
              <div className="basic-industry-issuers-section">
                {basicIndustryLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                  </div>
                ) : basicIndustryIssuers.length > 0 ? (
                  <Table
                    dataSource={basicIndustryIssuers}
                    rowKey={(record, index) => record.corporateIdentityNumber || index}
                    pagination={{
                      pageSize: 5,
                      showSizeChanger: false,
                      showTotal: (total) => `Total ${total} issuers`
                    }}
                    onRow={(record) => {
                      return {
                        onClick: () => {
                          const issuerCin = record.corporateIdentityNumber;
                          if (issuerCin) {
                            const isInDashboard = window.location.pathname.includes('/d/');
                            if (isInDashboard) {
                              navigate(`/d/issuer/details?issuerId=${encodeURIComponent(issuerCin)}`);
                            } else {
                              navigate(`/issuer-details/${encodeURIComponent(issuerCin)}`);
                            }
                            closeModal('basicIndustry');
                          }
                        },
                        style: { cursor: 'pointer' },
                        onMouseEnter: (e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        },
                        onMouseLeave: (e) => {
                          e.currentTarget.style.backgroundColor = '';
                        }
                      };
                    }}
                    columns={[
                      {
                        title: 'Issuer Name',
                        dataIndex: 'issuer',
                        key: 'issuer',
                        render: (text) => <Text strong>{text || 'N/A'}</Text>
                      },
                      {
                        title: 'Total Bond Borrowings',
                        dataIndex: 'totalBondBorrowings',
                        key: 'totalBondBorrowings',
                        render: (value) => (
                          <Tag color="geekblue">
                            {value ? `${(value).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr` : 'N/A'}
                          </Tag>
                        ),
                      }
                    ]}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Text type="secondary">No issuers found for this basic industry</Text>
                  </div>
                )}
              </div>
            </Modal>

            <Modal
              title={`Bond Borrowings - ${issuerData?.issuer || ''}`}
              open={modalVisible.bondBorrowings}
              onCancel={() => closeModal('bondBorrowings')}
              footer={null}
              width={800}
              bodyStyle={{ maxHeight: '500px', overflowY: 'auto' }}
            >
              <div className="bond-borrowings-section">
                {bondBorrowingsLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Spin size="large" />
                  </div>
                ) : bondBorrowingsData.length > 0 ? (
                  <Table
                    dataSource={bondBorrowingsData}
                    rowKey={(record, index) => record.isin || index}
                    pagination={{
                      pageSize: 5,
                      showSizeChanger: false,
                      showTotal: (total) => `Total ${total} ISINs`
                    }}
                    columns={[
                      {
                        title: 'ISIN',
                        dataIndex: 'isin',
                        key: 'isin',
                        render: (text) =>
                          text ? (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, fontWeight: 600 }}
                              onClick={() => {
                                closeModal('bondBorrowings');
                                navigate(`/isin-details/${encodeURIComponent(text)}`);
                              }}
                            >
                              {text}
                            </Button>
                          ) : (
                            <Text type="secondary">N/A</Text>
                          )
                      },
                      {
                        title: 'Amount Raised',
                        dataIndex: 'amountRaised',
                        key: 'amountRaised',
                        render: (amount) => (
                          <Text>
                            {amount ? (amount).toLocaleString('en-IN', { maximumFractionDigits: 2 }) + ' Cr' : 'N/A'}
                          </Text>
                        )
                      },
                      {
                        title: 'Coupon Rate',
                        dataIndex: 'couponRate',
                        key: 'couponRate',
                        render: (rate) => (
                          <Tag color="green">{rate ? `${rate}%` : 'N/A'}</Tag>
                        )
                      }
                    ]}
                  />
                ) : (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <Text type="secondary">No bond borrowings found</Text>
                  </div>
                )}
              </div>
            </Modal>

      </div>
    </div>
  );

  return skipLayout ? content : <LandingLayout>{content}</LandingLayout>;
};

export default IssuerDetails;
